import { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import * as XLSX from 'xlsx';

import {
  Grid,
  Typography,
  Box,
  Fade,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";

import { getSocket } from "../../services/socket";
import api from "../../services/api";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import HeaderManager from "../../components/HeaderTableManager/HeaderTableManager";
import TableManager from "../../components/TableManager/TableManager";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ImportExcelButton from "../../components/ImportExcelButton";
import NewTicketModal from "../../components/NewTicketModal";
import ContactModal from "../../components/ContactModal";

import TableWhatsAppIcon from "../../assets/icon/TableWhatsAppIcon";
import PencilEditIcon from "../../assets/icon/PencilEditIcon";
import ArrowDownIcon from "../../assets/icon/ArrowDownIcon";
import ArrowUpIcon from "../../assets/icon/ArrowUpIcon";
import TrashIcon from "../../assets/icon/TrashIcon";
import AddIcon from "../../assets/icon/AddIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import getTicketAccess from "../../utils/ticketAccess";
import { FileCopy, PhonelinkErase, QuestionAnswer, WhatsApp } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  tableContainer: {
    width: "100%",
    maxWidth: "80%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "90%",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "90%",
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "90%",
    },
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CONTACTS":
      return [
        ...state,
        ...action.payload.filter(
          (contact) => !state.find((c) => c.id === contact.id)
        ),
      ];
    case "UPDATE_CONTACT":
      return state.map((c) =>
        c.id === action.payload.id ? action.payload : c
      );
    case "DELETE_CONTACT":
      return state.filter((c) => c.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const ContactsTable = () => {
  const history = useHistory();
  const theme = useTheme();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [importOption, setImportOption] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false)
  const { user: { id: userId } } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    fetchContacts();
  }, [searchParam, page, rowsPerPage]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/", {
        params: { 
          searchParam,
          pageNumber: page + 1,
          limit: rowsPerPage
        },
      });
      
      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
      setTotalCount(data.count);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = getSocket(companyId);

    const handleEventContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    }

    socket.on(`company:contact`, handleEventContact);

    return () => {
      socket.off(`company:contact`, handleEventContact);
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
  };

  const handleOpenDeleteModal = (contact) => {
    setDeletingContact(contact);
    setDeleteConfirmOpen(true);
  };

  const handleOpenImportModal = () => {
    setImportOption(null);
    setImportConfirmOpen(true);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
    setNewTicketModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    setIsLoadingDelete(true);
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      await fetchContacts(); // Recargar contactos después de eliminar
    } catch (err) {
      toastError(err);
    } finally {
      setIsLoadingDelete(false);
      setDeletingContact(null);
      setDeleteConfirmOpen(false);
    }
  };

  const handleimportContact = async () => {
    setIsImporting(true);
    setImportConfirmOpen(false);
    try {
      await api.post("/contacts/import");
      toast.success(i18n.t("contacts.toasts.importSuccess"));
      await fetchContacts();
    } catch (err) {
      toastError(err);
    } finally {
      setIsImporting(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = (hasUpdated = false) => {
    setSelectedContactId(null);
    setContactModalOpen(false);
    if (hasUpdated) {
      setPageNumber(1);
      dispatch({ type: "RESET" });
      fetchContacts();
    }
  };
  const handleDownloadTemplate = () => {
    // Datos de ejemplo para la plantilla
    const templateData = [
      {
        name: "Nombre Ejemplo",
        number: "5491112345678",
        email: "ejemplo@email.com",
      },
    ];

    // Crear un nuevo libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Convertir los datos a una hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(templateData, {
      header: ["name", "number", "email"]
    });

    // Ajustar el ancho de las columnas
    const wscols = [
      { wch: 20 }, // name
      { wch: 15 }, // number
      { wch: 25 }, // email
    ];
    ws['!cols'] = wscols;

    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");

    // Generar el archivo y descargarlo
    XLSX.writeFile(wb, "plantilla_contactos.xlsx");
  };

  const primaryButtons = [
    {
      label: "Agregar Contacto",
      onClick: handleOpenContactModal,
      icon: <AddIcon fill="#000" />,
      style: { minWidth: '180px', justifyContent: 'flex-start', padding: '6px 16px' }
    },
  ];

  const secondaryButtons = [
    {
      label: "Importar Contactos",
      onClick: handleOpenImportModal,
      icon: <ArrowUpIcon fill={theme.palette.primary.main} />,
      style: { minWidth: '180px', justifyContent: 'flex-start', padding: '6px 16px' }
    },
  ];
  const csvData = {
    icon: <ArrowDownIcon fill={theme.palette.primary.main} />,
    label: "Exportar Contactos",
    data: contacts.map((contact) => ({
      name: contact.name,
      number: contact.number,
      email: contact.email,
    })),
    separator: ";",
    filename: "Contactos.csv",
  };
  const columns = [
    {
      field: "avatar",
      headerName: "Nombre",
      headerStyle: { fontWeight: "400" },
    },
    {
      field: "number",
      headerName: "WhatsApp",
      align: "center",
      width: "25%",
      headerStyle: { fontWeight: "400" },
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {row.number ? (
            <>
              {row.number}
              <Tooltip title="Copiar número">
                <IconButton size="small" onClick={() => {
                  navigator.clipboard.writeText(row.number)
                  toast.success('Número copiado al portapapeles')
                }}>
                  <FileCopy style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
              <PhonelinkErase style={{ fontSize: 18 }} />
              Sin número
            </span>
          )}
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Correo Electrónico",
      align: "center",
      width: "25%",
      headerStyle: { fontWeight: "400" },
    },
  ];

  const actionCellStyle = {
    width: "12%",
  };

  const actionButtons = [
    ({ row }) => (
      <IconButton
        size="small"
        onClick={async () => {
          try {
            const { data } = await api.get(`/tickets/contact/${row.id}`);
            const ticket = data?.ticket;

            if (ticket) {
              const access = getTicketAccess(ticket, { ...data?.user, id: userId });
              if (!access.canAccess) {
                return toast.warn("Sin acceso");
              }
              return history.push(`/tickets/${ticket.uuid}`);
            }

            setContactTicket(row);
            setNewTicketModalOpen(true);
          } catch (err) {
            setContactTicket(row);
            setNewTicketModalOpen(true);
          }
        }}
      >
        <Tooltip title={row.hasWhatsappAccount ? 'WhatsApp Válido' : 'WhatsApp Inválido'}>
          {row.channel === 'whatsapp' ? (
            <WhatsApp style={{ color: row.hasWhatsappAccount ? '#26B95C' : '#696969' }} />
          ) : (
            <QuestionAnswer style={{ color: row.hasWhatsappAccount ? '#2196F3' : '#696969' }} />
          )}
        </Tooltip>
      </IconButton>
    ),
    ({ row }) => (
      <IconButton size="small" onClick={() => hadleEditContact(row.id)}>
        <PencilEditIcon fill="#7F7F7F" />
      </IconButton>
    ),
    ({ row }) => (
      <IconButton size="small" onClick={() => handleOpenDeleteModal(row)}>
        <TrashIcon fill="#7F7F7F" />
      </IconButton>
    ),
  ];

  const ModalLoading = () => {
    return (
      <Fade in={true}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          minHeight="250px"
          style={{
            gap: "16px",
          }}
        >
          <CircularProgress size={50} thickness={4} />

          <Typography
            variant="body1"
            style={{
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            Procesando información...
          </Typography>
        </Box>
      </Fade>
    );
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <div className={classes.mainContainer}>
        <ConfirmationModal
          title="Eliminar Contacto"
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => handleDeleteContact(deletingContact?.id)}
          isLoadingConfirmation={isLoadingDelete}
          onCloseAfterConfirming={false}
        >
          ¿Estás seguro de que deseas eliminar este contacto?
        </ConfirmationModal>

        <ContactModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          aria-labelledby="form-dialog-title"
          contactId={selectedContactId}
        ></ContactModal>

        <NewTicketModal
          modalOpen={newTicketModalOpen}
          initialContact={contactTicket}
          onClose={(ticket) => {
            handleCloseOrOpenTicket(ticket);
          }}
        />

        <ConfirmationModal
          title="Importar Contactos"
          open={importConfirmOpen}
          onClose={() => setImportConfirmOpen(false)}
          maxWidth="sm"
          minWidth="600px"
          minHeight="200px"
          showConfirmButton={false}
        >
          {
            !isImporting ? (<Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px'
            }}
          >
            <Typography 
              variant="body1" 
              align="center" 
              color="textSecondary"
              style={{ marginBottom: '24px', maxWidth: '500px' }}
            >
              Selecciona un archivo Excel o CSV para importar tus contactos. Descarga la plantilla para empezar a agregar tus contactos.
            </Typography>

            <Grid
              container
              spacing={3}
              justifyContent="space-between"
              alignItems="center"
              style={{ width: '100%', maxWidth: '500px' }}
            >
              <Grid 
                item 
                xs={12} 
                sm={4}
                style={{ 
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Button
                  onClick={handleDownloadTemplate}
                  variant="contained"
                  color="primary"
                  disabled={isImporting}
                  style={{
                    width: '100%',
                    height: '45px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.85rem',
                    padding: '6px 16px'
                  }}
                >
                  Descargar plantilla
                </Button>
              </Grid>
              <Grid 
                item 
                xs={12} 
                sm={4}
                style={{ 
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <ImportExcelButton
                  endpoint="/contacts/import-excel"
                  changeLoading={(value) => { setIsImporting(value) }}
                  onSuccess={(data) => {
                    toast.success(i18n.t("contacts.toasts.importSuccess"));
                    dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
                    setImportConfirmOpen(false);
                  }}
                  onError={(err) => {
                    toastError(err);
                    setImportConfirmOpen(false);
                  }}
                  buttonText="Importar Excel"
                  variant="contained"
                  color="primary"
                  disabled={isImporting}
                  style={{
                    width: '100%',
                    height: '45px',
                    fontSize: '0.85rem',
                    padding: '6px 16px',
                  }}
                />
              </Grid>
              <Grid 
                item 
                xs={12} 
                sm={4}
                style={{ 
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Button
                  onClick={handleimportContact}
                  variant="contained"
                  color="primary"
                  disabled={isImporting}
                  style={{
                    width: '100%',
                    height: '45px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.85rem',
                  }}
                >
                  {isImporting ? "Importando..." : "Importar WhatsApp"}
                </Button>
              </Grid>
            </Grid>
          </Box>) : (<ModalLoading />)
          }
          
        </ConfirmationModal>

        <div className={classes.tableContainer}>
          <HeaderManager
            title="Contactos"
            primaryButtons={primaryButtons}
            secondaryButtons={secondaryButtons}
            onSearch={handleSearch}
            csvData={csvData}
          />
          <TableManager
            columns={columns}
            actionCellStyle={actionCellStyle}
            data={contacts}
            loading={loading}
            actionButtons={actionButtons}
            hasPagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>
    </>
  );
};

export default ContactsTable;
